// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * Page heading for an administration screen.
 *
 * Each screen renders exactly one `h1` so heading order stays predictable for
 * screen-reader navigation (`docs/10-TESTING.md` §30, semantic roles and
 * names). The optional scope line states which tenant the data belongs to,
 * which is how an operator can tell two similar screens apart.
 */

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';

/** Props for {@link PageHeader}. */
export interface PageHeaderProps {
  /** Page title rendered as the single `h1`. */
  readonly title: string;
  /** Short description of what the screen shows. */
  readonly description?: string;
  /** Human-readable scope, for example the tenant name. */
  readonly scopeLabel?: string;
  /** Optional trailing controls such as filters. */
  readonly actions?: ReactNode;
}

/** Render the standard administration page header. */
export function PageHeader({
  title,
  description,
  scopeLabel,
  actions,
}: PageHeaderProps): ReactNode {
  return (
    <Box component="header" sx={{ mb: 3 }}>
      <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="h1" component="h1">
            {title}
          </Typography>
          {scopeLabel === undefined ? null : (
            <Typography variant="body2" color="text.secondary">
              Scope: {scopeLabel}
            </Typography>
          )}
          {description === undefined ? null : (
            <Typography variant="body1" sx={{ mt: 1 }}>
              {description}
            </Typography>
          )}
        </Box>
        {actions === undefined ? null : <Box>{actions}</Box>}
      </Stack>
    </Box>
  );
}
